import { useState, useEffect } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AlertTriangle, X, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { creditsEvents } from '@/utils/creditsEvents';

interface CreditSummary {
  total: number;
  low_credit_warning: boolean;
  current_cycle?: {
    cycle_end_date: string;
  };
}

export function LowCreditBanner() {
  const [credits, setCredits] = useState<CreditSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // EMERGENCY CIRCUIT BREAKER: Disable auto-loading credits
    // fetchCredits();
    setLoading(false);
    
    // // Listen for credit updates
    // const unsubscribe = creditsEvents.subscribe((event) => {
    //   if (event.userId === user?.id) {
    //     fetchCredits();
    //   }
    // });

    // return unsubscribe;
  }, [user?.id]);

  useEffect(() => {
    // Check if banner was dismissed for current cycle
    if (credits?.current_cycle?.cycle_end_date && user?.id) {
      const dismissKey = `credit-banner-dismissed-${user.id}-${credits.current_cycle.cycle_end_date}`;
      const wasDismissed = localStorage.getItem(dismissKey) === 'true';
      setDismissed(wasDismissed);
    } else if (credits && user?.id) {
      // Fallback for cycles without end date - use today's date
      const today = new Date().toISOString().split('T')[0];
      const dismissKey = `credit-banner-dismissed-${user.id}-${today}`;
      const wasDismissed = localStorage.getItem(dismissKey) === 'true';
      setDismissed(wasDismissed);
    }
  }, [credits, user?.id]);

  const fetchCredits = async () => {
    try {
      // Check if credits system is enabled
      const { data: settings } = await supabase
        .from('system_settings')
        .select('setting_value')
        .eq('setting_key', 'credits_enabled')
        .single();
      
      if (settings?.setting_value !== 'true') {
        setCredits(null);
        return;
      }

      const { data, error } = await supabase.functions.invoke('get-credits');
      
      if (error || !data) {
        setCredits(null);
        return;
      }

      setCredits(data);
    } catch (error) {
      console.error('Error fetching credits for banner:', error);
      setCredits(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    if (user?.id && credits) {
      const dismissKey = credits.current_cycle?.cycle_end_date 
        ? `credit-banner-dismissed-${user.id}-${credits.current_cycle.cycle_end_date}`
        : `credit-banner-dismissed-${user.id}-${new Date().toISOString().split('T')[0]}`;
      
      localStorage.setItem(dismissKey, 'true');
      setDismissed(true);
    }
  };

  const handleViewDetails = () => {
    navigate('/profile?tab=credits');
  };

  // Don't show if loading, no credits data, not low credit warning, or dismissed
  if (loading || !credits || !credits.low_credit_warning || dismissed) {
    return null;
  }

  return (
    <Alert variant="destructive" className="mb-6">
      <AlertTriangle className="h-4 w-4" />
      <AlertDescription className="flex items-center justify-between">
        <div>
          <strong>Low Credit Warning:</strong> You have {credits.total || 0} credits remaining. 
          Consider purchasing more credits to avoid service interruption.
        </div>
        <div className="flex items-center gap-2 ml-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewDetails}
            className="text-destructive hover:text-destructive"
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            View Details
          </Button>
          <Button
            variant="ghost" 
            size="sm"
            onClick={handleDismiss}
            className="text-destructive hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </AlertDescription>
    </Alert>
  );
}